/**
 * English is the source locale. Every key here must exist before it is
 * translated; `es` and `ru` are checked against this file by
 * `pnpm --filter @prody/web test i18n`.
 *
 * Copy rules for this product:
 *  - Name things the way the trade does: albarán, calibre, stand, run.
 *  - An action keeps the same verb through the whole flow. The button
 *    says "Place order", the toast says "Order placed".
 *  - Errors say what happened and what to do. They do not apologise.
 *  - Empty states are an instruction, not a mood.
 */
export default {
  common: {
    app: 'Prody',
    actions: {
      save: 'Save',
      saveChanges: 'Save changes',
      cancel: 'Cancel',
      close: 'Close',
      create: 'Create',
      edit: 'Edit',
      delete: 'Delete',
      duplicate: 'Duplicate',
      confirm: 'Confirm',
      retry: 'Try again',
      undo: 'Undo',
      search: 'Search',
      filter: 'Filter',
      clearFilters: 'Clear filters',
      export: 'Export',
      print: 'Print',
      add: 'Add',
      remove: 'Remove',
      assign: 'Assign',
      openInNew: 'Open in new tab',
      more: 'More actions',
      back: 'Back',
      next: 'Next',
      selectAll: 'Select all',
      copy: 'Copy',
      copied: 'Copied',
    },
    labels: {
      all: 'All',
      none: 'None',
      today: 'Today',
      tomorrow: 'Tomorrow',
      yesterday: 'Yesterday',
      thisWeek: 'This week',
      total: 'Total',
      subtotal: 'Subtotal',
      tax: 'Tax',
      surcharge: 'Surcharge',
      discount: 'Discount',
      net: 'Net',
      gross: 'Gross',
      quantity: 'Qty',
      unit: 'Unit',
      price: 'Price',
      cost: 'Cost',
      margin: 'Margin',
      status: 'Status',
      date: 'Date',
      time: 'Time',
      window: 'Window',
      notes: 'Notes',
      internalNote: 'Internal note',
      customerNote: 'Customer note',
      createdBy: 'Created by',
      updated: 'Updated',
      assignedTo: 'Assigned to',
      customer: 'Customer',
      supplier: 'Supplier',
      product: 'Product',
      sku: 'SKU',
      selected_one: '{{count}} selected',
      selected_other: '{{count}} selected',
      results_one: '{{count}} result',
      results_other: '{{count}} results',
      of: 'of',
    },
    nav: {
      trade: 'Trade',
      move: 'Move',
      people: 'People',
      money: 'Money',
      manage: 'Manage',
      collapse: 'Collapse navigation',
      expand: 'Expand navigation',
      favorites: 'Pinned',
      pin: 'Pin to favourites',
      unpin: 'Unpin',
      dock: 'Navigation position',
      dockLeft: 'Left edge',
      dockRight: 'Right edge',
      dockTop: 'Top, as a navbar',
      dockBottom: 'Bottom, as a taskbar',
    },
    search: {
      placeholder: 'Search orders, products, contacts…',
      openPalette: 'Open command palette',
      noResults: 'Nothing matches “{{query}}”.',
      hint: 'Type at least two characters.',
      commands: 'Commands',
      recent: 'Recent',
    },
    appearance: {
      theme: 'Theme',
      themeHall: 'Hall',
      themeHallHint: 'Dark. Built for the market floor and the van.',
      themeOffice: 'Office',
      themeOfficeHint: 'Light. Built for the desk and the printer.',
      themeAuto: 'Match system',
      density: 'Density',
      densityCompact: 'Compact',
      densityComfortable: 'Comfortable',
      densityMarket: 'Market',
      densityMarketHint: 'Large targets and fewer columns, for gloved hands.',
      language: 'Language',
    },
    handlingClass: {
      ambient: 'Ambient',
      chilled: 'Chilled',
      frozen: 'Frozen',
      fresh: 'Fresh',
      live: 'Live',
      hazmat: 'Hazardous',
      neutral: 'Standard',
      legend: 'Colour shows how the goods are handled, not their status.',
    },
    channel: {
      whatsapp: 'WhatsApp',
      telegram: 'Telegram',
      email: 'Email',
      sms: 'SMS',
      phone: 'Phone',
      signal: 'Signal',
      instagram: 'Instagram',
    },
    role: {
      owner: 'Owner',
      manager: 'Manager',
      sales: 'Sales',
      buyer: 'Buyer',
      driver: 'Driver',
      warehouse: 'Warehouse',
      accountant: 'Accountant',
      viewer: 'Viewer',
    },
    business: {
      switch: 'Switch business',
      current: 'Current business',
      addNew: 'Connect a business',
    },
    errors: {
      loadFailed: 'Could not load this list.',
      loadFailedBody: 'The server did not respond. Check your connection and try again.',
      saveFailed: 'Could not save.',
      notFound: 'Not found',
      notFoundBody: 'This record does not exist, or it belongs to another business.',
      forbidden: 'You do not have access to this',
      forbiddenBody: 'Ask an owner or manager to change your role.',
      offline: 'You are offline. Changes are queued and will send when the connection returns.',
      required: 'Required',
      invalidNumber: 'Enter a number',
      mustBePositive: 'Must be greater than zero',
    },
    empty: {
      noResults: 'Nothing matches these filters',
      noResultsBody: 'Widen the date range or clear a filter.',
    },
  },

  dashboard: {
    title: 'Today',
    eyebrow: 'Overview',
    tradingDay: 'Trading day {{date}}',
    openOrders: 'Open orders',
    toBuy: 'Still to buy',
    outForDelivery: 'Out for delivery',
    overdue: 'Overdue receivable',
    fillRate: 'Fill rate',
    fillRateFoot: 'Ordered vs delivered, last 7 days',
    shortLines: 'Short lines today',
    priceMoves: 'Price moves this morning',
    empty: 'Nothing scheduled for today.',
    emptyBody: 'Orders placed before the {{cutoff}} cutoff appear here.',
  },

  orders: {
    title: 'Orders',
    eyebrow: 'Trade',
    nav: 'Orders',
    number: 'Order',
    newOrder: 'New order',
    placeOrder: 'Place order',
    orderPlaced: 'Order {{number}} placed',
    deliveryDate: 'Delivery',
    channel: 'Received via',
    lines_one: '{{count}} line',
    lines_other: '{{count}} lines',
    fillRate: 'Filled',
    status: {
      draft: 'Draft',
      placed: 'Placed',
      sourcing: 'Sourcing',
      picking: 'Picking',
      ready: 'Ready',
      dispatched: 'Out',
      delivered: 'Delivered',
      invoiced: 'Invoiced',
      cancelled: 'Cancelled',
    },
    channels: {
      portal: 'Portal',
      whatsapp: 'WhatsApp',
      telegram: 'Telegram',
      phone: 'Phone',
      email: 'Email',
      'sales-app': 'Sales app',
      recurring: 'Recurring',
    },
    searchPlaceholder: 'Order, client, product…',
    filters: {
      day: 'Delivery day',
      status: 'Status',
      customer: 'Customer',
      shortOnly: 'Short only',
      openOnly: 'Open',
      unassigned: 'Unassigned',
    },
    columns: {
      tradeName: 'Client code',
      contact: 'Ordered by',
      paid: 'Paid',
      balance: 'Balance',
      payment: 'Payment',
      sold: 'Sold',
      cogs: 'Cost',
      profit: 'Profit',
      rows: 'Rows',
    },
    detail: {
      lines: 'Lines',
      addLine: 'Add line',
      ordered: 'Ordered',
      picked: 'Picked',
      invoiced: 'Invoiced',
      shortfall: 'Short',
      substitute: 'Substitute',
      recordShortfall: 'Record shortfall',
      shortfallRecorded: 'Shortfall recorded on {{product}}',
      reason: 'Reason',
      reasonHint: 'The buyer reads this tomorrow morning. Be specific.',
      substitutionFor: 'Instead of {{product}}',
      priceFrom: 'Price from',
      priceSource: {
        personal: 'Personal price',
        'price-list': 'Price list · {{name}}',
        market: 'Market {{date}}',
        base: 'Catalog',
        manual: 'Typed by hand',
      },
      timeline: 'History',
      conversation: 'Conversation',
      documents: 'Documents',
      openFull: 'Open order',
      shipment: 'Albarán',
      placedAt: 'Placed',
      unassigned: 'No supplier',
      supplierAssigned: 'Supplier set on {{product}}',
      address: 'Delivery address',
      terminal: 'Terminal',
      settled: 'Settled',
      requested: 'Asked for',
    },
    editor: {
      titleEdit: 'Edit order',
      saveDraft: 'Save draft',
      saved: 'Order {{number}} saved',
      selectCustomer: 'Choose a customer…',
      selectProduct: 'Choose a product…',
      noLines: 'Add at least one line with a product and a quantity.',
      notePlaceholder: '“Green ones, not ripe”',
      windowHint: 'HH:mm–HH:mm',
      invalidWindow: 'The window must end after it starts',
      windowFrom: 'From',
      windowTo: 'To',
      removeLine: 'Remove line',
    },
    shortfallReason: {
      'out-of-stock': 'Out of stock',
      'quality-rejected': 'Quality rejected',
      'supplier-short': 'Market was short',
      'price-refused': 'Customer refused price',
      damaged: 'Damaged',
      other: 'Other',
    },
    empty: 'No orders for this day',
    emptyBody: 'Orders appear here as they arrive, from the portal or from a message.',
    cancelTitle: 'Cancel order {{number}}?',
    cancelBody:
      'The customer keeps the record and the lines stay visible, but nothing will be picked or delivered.',
    cancelConfirm: 'Cancel order',
  },

  grouping: {
    title: 'Grouping',
    eyebrow: 'Trade',
    nav: 'Grouping',
    view: 'View',
    byProduct: 'By product',
    bySupplier: 'By supplier',
    products: 'Products',
    unassigned: 'No supplier',
    covered: 'Covered',
    estimated: 'Est. cost',
    total: 'Day total',
    assigned: 'Supplier set on {{product}}',
    ordersTouched_one: '{{count}} order',
    ordersTouched_other: '{{count}} orders',
    lineCount_one: '{{count}} product',
    lineCount_other: '{{count}} products',
    sendToBuying: 'Send to Buying',
    sentToBuying_one: '{{count}} task sent to Buying',
    sentToBuying_other: '{{count}} tasks sent to Buying',
    empty: 'No demand for this day',
    emptyBody: 'Confirmed orders feed this board. Take one and the products appear.',
  },

  market: {
    title: 'Market board',
    eyebrow: 'Trade',
    nav: 'Market',
    tradingDay: 'Trading day',
    yesterday: 'Yesterday',
    todayCost: 'Cost today',
    todaySell: 'Sell today',
    change: 'Change',
    availability: 'Supply',
    availabilityValue: {
      plenty: 'Plenty',
      tight: 'Tight',
      scarce: 'Scarce',
      none: 'None',
    },
    publish: 'Publish prices',
    published: 'Prices published for {{date}}',
    publishHint: 'Customers see the new sell price the moment you publish.',
    unpublished_one: '{{count}} price not published',
    unpublished_other: '{{count}} prices not published',
    empty: 'No quotes for this day',
    emptyBody: 'Start the day by copying yesterday’s board, then edit what moved.',
    copyYesterday: 'Copy yesterday',
    marginWarning: 'Selling below cost',
  },

  purchasing: {
    title: 'Buying',
    eyebrow: 'Move',
    nav: 'Buying',
    shoppingList: 'Shopping list',
    required: 'Need',
    onHand: 'In stock',
    toBuy: 'To buy',
    bought: 'Bought',
    stand: 'Stand',
    markBought: 'Bought',
    markUnavailable: 'Not available',
    unavailableWarning_one: '{{count}} order affected',
    unavailableWarning_other: '{{count}} orders affected',
    demandFrom: 'Needed by',
    generate: 'Build list from orders',
    generated: 'List built from {{count}} orders',
    runs: 'Farm runs',
    empty: 'Nothing to buy',
    emptyBody: 'Build the list once the day’s orders are in.',
  },

  logistics: {
    title: 'Logistics',
    eyebrow: 'Move',
    nav: 'Logistics',
    routes: 'Routes',
    route: 'Route',
    stops_one: '{{count}} stop',
    stops_other: '{{count}} stops',
    driver: 'Driver',
    vehicle: 'Vehicle',
    dispatch: 'Dispatch route',
    dispatched: 'Route {{name}} dispatched',
    eta: 'ETA',
    crates: 'Crates',
    cratesReturned: 'Returned',
    proof: 'Proof of delivery',
    signedBy: 'Signed by',
    stopStatus: {
      pending: 'Pending',
      'en-route': 'En route',
      arrived: 'Arrived',
      delivered: 'Delivered',
      partial: 'Partial',
      failed: 'Failed',
      skipped: 'Skipped',
    },
    empty: 'No routes planned',
    emptyBody: 'Group ready orders into a route to send a van out.',
  },

  suppliers: {
    title: 'Suppliers',
    eyebrow: 'Move',
    nav: 'Suppliers',
    searchPlaceholder: 'Name, code, stand…',
    sourceKind: {
      market: 'Market stand',
      farm: 'Farm',
      importer: 'Importer',
      manufacturer: 'Manufacturer',
      other: 'Other',
    },
    needsToday_one: '{{count}} needed',
    needsToday_other: '{{count}} needed',
    nothingToday: 'Nothing needed',
    bringFor: 'To bring for',
    cutoff: 'Order cutoff',
    orderCount_one: '{{count}} order',
    orderCount_other: '{{count}} orders',
    sheetEmpty: 'Nothing to bring this day',
    sheetEmptyBody: 'Assign this supplier on the grouping board and the sheet fills itself.',
    weekday: {
      mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun',
    },
    empty: 'No suppliers yet',
    emptyBody: 'Give a contact the supplier role and it appears here.',
  },

  deliveries: {
    title: 'Delivery list',
    eyebrow: 'Move',
    nav: 'Delivery list',
    albaran: 'Albarán',
    stops: 'Deliveries',
    incidents: 'Incidents',
    noRoute: 'No route',
    lineCount_one: '{{count}} line',
    lineCount_other: '{{count}} lines',
    laneEmpty: 'No stops on this route yet.',
    goods: 'Goods to deliver',
    assignedToRoute: 'Assigned to {{route}}',
    routeStatus: {
      planning: 'Planning',
      dispatched: 'On the road',
      completed: 'Completed',
      cancelled: 'Cancelled',
    },
    empty: 'No deliveries for this day',
    emptyBody: 'Group ready orders into shipments and the sheet fills itself.',
  },

  catalog: {
    title: 'Catalog',
    eyebrow: 'Trade',
    nav: 'Catalog',
    products: 'Products',
    categories: 'Categories',
    unitMode: {
      each: 'By piece',
      weight: 'By weight',
      volume: 'By volume',
      length: 'By length',
    },
    catchWeight: 'Catch weight',
    catchWeightHint: 'Ordered by piece, invoiced on the weighed amount.',
    shelfLife: 'Shelf life',
    shelfLifeDays_one: '{{count}} day',
    shelfLifeDays_other: '{{count}} days',
    origin: 'Origin',
    calibre: 'Calibre',
    substitutes: 'Substitutes',
    substitutesHint: 'Offered to a picker when this product is short, in this order.',
    status: {
      active: 'Active',
      'seasonal-off': 'Out of season',
      discontinued: 'Discontinued',
      draft: 'Draft',
    },
    empty: 'No products yet',
    emptyBody: 'Add your first product, or import a supplier price list.',
  },

  products: {
    title: 'Product manager',
    nav: 'Products',
    newProduct: 'New product',
    created: '{{name}} created',
    personalPrices: 'Personal prices',
    priceLists: 'Price lists',
  },

  stock: {
    title: 'Stock',
    eyebrow: 'Move',
    nav: 'Stock',
    onHand: 'On hand',
    reserved: 'Reserved',
    available: 'Available',
    lots_one: '{{count}} lot',
    lots_other: '{{count}} lots',
    nextExpiry: 'Next expiry',
    expiringSoon_one: '{{count}} lot expiring',
    expiringSoon_other: '{{count}} lots expiring',
    valuation: 'Value',
    writeOff: 'Write off',
    count: 'Stock count',
    empty: 'Nothing in stock',
    emptyBody: 'Stock appears here once a purchase is received.',
  },

  contacts: {
    title: 'Contacts',
    eyebrow: 'People',
    nav: 'Contacts',
    roles: {
      customer: 'Customer',
      supplier: 'Supplier',
      ambassador: 'Ambassador',
      carrier: 'Carrier',
      partner: 'Partner',
    },
    newContact: 'New contact',
    people: 'People',
    addresses: 'Addresses',
    deliveryWindows: 'Delivery windows',
    terms: 'Payment terms',
    netDays: 'Net {{days}} days',
    onDelivery: 'On delivery',
    creditLimit: 'Credit limit',
    overLimit: 'Over credit limit',
    balance: 'Balance',
    lastOrder: 'Last order',
    referredBy: 'Introduced by',
    commission: 'Commission',
    runDays: 'Run days',
    standCode: 'Stand',
    empty: 'No contacts yet',
    emptyBody: 'Add a customer or a supplier to start trading.',
  },

  messaging: {
    title: 'Messages',
    eyebrow: 'People',
    nav: 'Messages',
    allChannels: 'All channels',
    reply: 'Reply',
    send: 'Send',
    sent: 'Sent',
    composerPlaceholder: 'Write to {{name}} on {{channel}}…',
    windowClosed: 'The {{channel}} reply window has closed',
    windowClosedBody: 'You can only send an approved template until {{name}} writes again.',
    useTemplate: 'Use a template',
    voiceNote: 'Voice note',
    transcript: 'Transcript',
    assignTo: 'Assign to',
    snooze: 'Snooze',
    closeThread: 'Close',
    unread_one: '{{count}} unread',
    unread_other: '{{count}} unread',
    empty: 'No conversations',
    emptyBody: 'Messages from every channel land here, grouped by contact.',
  },

  schedule: {
    title: 'Schedule',
    eyebrow: 'People',
    nav: 'Schedule',
    newAppointment: 'New appointment',
    kind: {
      'sales-visit': 'Sales visit',
      tasting: 'Tasting',
      'farm-visit': 'Farm visit',
      'delivery-slot': 'Delivery slot',
      collection: 'Collection',
      internal: 'Internal',
      call: 'Call',
    },
    status: {
      scheduled: 'Scheduled',
      confirmed: 'Confirmed',
      done: 'Done',
      'no-show': 'No show',
      cancelled: 'Cancelled',
    },
    empty: 'Nothing scheduled',
    emptyBody: 'Book a visit or a tasting and it appears here.',
  },

  invoices: {
    title: 'Invoices',
    eyebrow: 'Money',
    nav: 'Invoices',
    sales: 'Sales',
    purchase: 'Purchase',
    issue: 'Issue invoice',
    issued: 'Invoice {{number}} issued',
    send: 'Send invoice',
    due: 'Due',
    dueIn_one: 'Due in {{count}} day',
    dueIn_other: 'Due in {{count}} days',
    overdueBy_one: '{{count}} day late',
    overdueBy_other: '{{count}} days late',
    outstanding: 'Outstanding',
    creditNote: 'Credit note',
    status: {
      draft: 'Draft',
      issued: 'Issued',
      sent: 'Sent',
      'part-paid': 'Part paid',
      paid: 'Paid',
      overdue: 'Overdue',
      disputed: 'Disputed',
      void: 'Void',
    },
    empty: 'No invoices',
    emptyBody: 'Invoice a delivered order to create the first one.',
  },

  payments: {
    title: 'Payments',
    eyebrow: 'Money',
    nav: 'Payments',
    receivable: 'They owe us',
    payable: 'We owe',
    recordPayment: 'Record payment',
    recorded: 'Payment of {{amount}} recorded',
    allocate: 'Allocate',
    unallocated: 'Unallocated',
    ageing: 'Ageing',
    buckets: {
      current: 'Current',
      d1_30: '1–30 d',
      d31_60: '31–60 d',
      d61_90: '61–90 d',
      d90plus: '90 d +',
    },
    method: {
      cash: 'Cash',
      transfer: 'Transfer',
      card: 'Card',
      'direct-debit': 'Direct debit',
      offset: 'Offset',
      other: 'Other',
    },
    empty: 'Nothing outstanding',
    emptyBody: 'Every invoice is settled.',
  },

  analytics: {
    title: 'Analytics',
    eyebrow: 'Money',
    nav: 'Analytics',
    revenue: 'Revenue',
    margin: 'Margin',
    topProducts: 'Top products',
    topCustomers: 'Top customers',
    fillRate: 'Fill rate',
    shortfalls: 'Shortfalls by reason',
    period: 'Period',
    empty: 'Not enough data yet',
    emptyBody: 'Charts appear once there is a week of trading to compare.',
  },

  team: {
    title: 'Team',
    eyebrow: 'Manage',
    nav: 'Team',
    invite: 'Invite someone',
    invited: 'Invitation sent to {{email}}',
    role: 'Role',
    lastSeen: 'Last seen',
    status: {
      invited: 'Invited',
      active: 'Active',
      disabled: 'Disabled',
    },
    empty: 'Just you so far',
    emptyBody: 'Invite a buyer, a driver or an accountant and give them a role.',
  },

  settings: {
    title: 'Settings',
    eyebrow: 'Manage',
    nav: 'Settings',
    general: 'General',
    localisation: 'Currency, time and language',
    tax: 'Tax',
    modules: 'Modules',
    modulesHint: 'Turn off what this business does not use. Navigation follows.',
    currency: 'Currency',
    timezone: 'Timezone',
    formatLocale: 'Number and date format',
    weekStart: 'Week starts on',
    dayStartsAt: 'Trading day starts at',
    dayStartsAtHint: 'Orders taken before this time belong to the previous trading day.',
    orderCutoff: 'Order cutoff',
    orderCutoffHint: 'After this time, new orders move to the next delivery day.',
    weightPrecision: 'Weight precision',
    saved: 'Settings saved',
  },
  // Deliberately not `as const`: `typeof en` is the contract the other
  // locales are checked against, and literal value types would demand
  // that Spanish say "Save".
};
